import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  LinkIcon,
  FileText,
  LogOut,
  Upload,
  Trash2,
  Plus,
  Save,
  X,
  ExternalLink,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "site", label: "Site", icon: Settings },
  { id: "links", label: "Links", icon: LinkIcon },
  { id: "info", label: "Informações", icon: FileText },
];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("site");
  const [data, setData] = useState({
    site: { name: "", tagline: "", logo_data_url: null },
    links: [],
    info_blocks: [],
  });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const res = await api.get("/site");
    setData(res.data);
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <Toaster position="top-right" theme="dark" />
      {/* Sidebar */}
      <aside className="hidden w-[250px] shrink-0 flex-col border-r border-white/10 bg-black/60 p-6 md:flex">
        <div className="mb-10 flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(57,255,20,0.8)]" />
          <span className="font-display text-sm font-black uppercase tracking-[0.2em]">
            console
          </span>
        </div>
        <nav className="flex flex-col gap-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left font-mono text-sm transition ${
                tab === id
                  ? "border border-cyan-400/40 bg-cyan-400/10 text-cyan-300"
                  : "border border-transparent text-zinc-400 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t border-white/10 pt-4">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            sessão
          </p>
          <p className="mb-3 truncate font-mono text-xs text-zinc-300">{user?.email}</p>
          <button
            onClick={doLogout}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 p-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition hover:border-red-400/40 hover:text-red-300"
            data-testid="logout-button"
          >
            <LogOut className="h-3 w-3" />
            sair
          </button>
        </div>
      </aside>

      {/* Mobile tabs */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-black/90 backdrop-blur-md">
        <div className="grid grid-cols-4 gap-1 p-2">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex flex-col items-center gap-1 rounded-md p-2 font-mono text-[10px] uppercase tracking-wider ${
                tab === id ? "text-cyan-300" : "text-zinc-500"
              }`}
              data-testid={`tab-mobile-${id}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
          <button
            onClick={doLogout}
            className="flex flex-col items-center gap-1 rounded-md p-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500"
            data-testid="logout-mobile-button"
          >
            <LogOut className="h-4 w-4" />
            sair
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-auto p-6 pb-28 sm:p-8">
        <header className="mb-8">
          <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            painel administrativo
          </p>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            {TABS.find((t) => t.id === tab)?.label}
          </h1>
        </header>

        {loading ? (
          <div className="font-mono text-sm text-zinc-500">Carregando...</div>
        ) : tab === "site" ? (
          <SiteSettings data={data} onChange={refresh} />
        ) : tab === "links" ? (
          <LinksManager data={data} onChange={refresh} />
        ) : (
          <InfoManager data={data} onChange={refresh} />
        )}
      </main>
    </div>
  );
}

/* -------- Site Settings -------- */
function SiteSettings({ data, onChange }) {
  const [name, setName] = useState(data.site?.name || "");
  const [tagline, setTagline] = useState(data.site?.tagline || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setName(data.site?.name || "");
    setTagline(data.site?.tagline || "");
  }, [data.site?.name, data.site?.tagline]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site", { name, tagline });
      toast.success("Configurações salvas");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/site/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo atualizado");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/site/logo");
      toast.success("Logo removido");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Logo card */}
      <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-6">
        <h3 className="mb-1 font-display text-lg font-bold">Logo</h3>
        <p className="mb-4 font-mono text-xs text-zinc-500">
          PNG/JPG até 3MB. Aparece na página pública.
        </p>
        <div className="mb-4 flex items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/40 p-6">
          {data.site?.logo_data_url ? (
            <img
              src={data.site.logo_data_url}
              alt="logo atual"
              className="h-32 w-32 rounded-lg object-cover"
              data-testid="current-logo-preview"
            />
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-lg border border-white/5 bg-white/5 font-mono text-xs text-zinc-500">
              sem logo
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <label
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md bg-cyan-400 p-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition hover:bg-emerald-400"
            data-testid="logo-upload-label"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "..." : "enviar"}
            <input
              type="file"
              accept="image/*"
              onChange={onLogoUpload}
              className="hidden"
              data-testid="logo-upload-input"
            />
          </label>
          {data.site?.logo_data_url && (
            <button
              onClick={removeLogo}
              className="rounded-md border border-white/10 bg-white/5 p-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition hover:border-red-400/40 hover:text-red-300"
              data-testid="logo-remove-button"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Name & tagline */}
      <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-6">
        <h3 className="mb-4 font-display text-lg font-bold">Identidade</h3>
        <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
          Nome do site
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-4 w-full rounded-md border border-white/15 bg-black p-3 font-mono text-sm outline-none transition focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
          placeholder="CHEAT or not"
          data-testid="site-name-input"
        />
        <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
          Tagline (opcional)
        </label>
        <textarea
          value={tagline || ""}
          onChange={(e) => setTagline(e.target.value)}
          rows={3}
          className="mb-4 w-full resize-none rounded-md border border-white/15 bg-black p-3 font-mono text-sm outline-none transition focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
          placeholder="frase curta abaixo do nome..."
          data-testid="site-tagline-input"
        />
        <button
          onClick={save}
          disabled={saving}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-cyan-400 p-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-black transition hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] disabled:opacity-60"
          data-testid="site-save-button"
        >
          <Save className="h-4 w-4" />
          {saving ? "salvando..." : "salvar"}
        </button>
      </div>
    </div>
  );
}

/* -------- Links Manager -------- */
function LinksManager({ data, onChange }) {
  const [editing, setEditing] = useState(null); // null | "new" | id
  const empty = {
    title: "",
    url: "",
    description: "",
    order: 0,
    file_id: null,
    file_name: null,
    file_size: null,
  };
  const [form, setForm] = useState(empty);
  const [mode, setMode] = useState("url"); // "url" | "file"
  const [uploading, setUploading] = useState(false);

  const startNew = () => {
    setEditing("new");
    setForm({ ...empty, order: data.links.length });
    setMode("url");
  };
  const startEdit = (link) => {
    setEditing(link.id);
    setForm({
      title: link.title,
      url: link.url || "",
      description: link.description || "",
      order: link.order ?? 0,
      file_id: link.file_id || null,
      file_name: link.file_name || null,
      file_size: link.file_size || null,
    });
    setMode(link.file_id ? "file" : "url");
  };
  const cancel = () => {
    setEditing(null);
    setForm(empty);
    setMode("url");
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        file_id: res.data.file_id,
        file_name: res.data.file_name,
        file_size: res.data.file_size,
        url: "",
      }));
      toast.success(`Arquivo enviado (${formatBytes(res.data.file_size)})`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Preencha o título");
      return;
    }
    if (mode === "url" && !form.url.trim()) {
      toast.error("Preencha a URL");
      return;
    }
    if (mode === "file" && !form.file_id) {
      toast.error("Envie um arquivo");
      return;
    }
    const payload =
      mode === "url"
        ? {
            title: form.title,
            url: form.url,
            description: form.description,
            order: form.order,
            file_id: null,
            file_name: null,
            file_size: null,
          }
        : {
            title: form.title,
            url: null,
            description: form.description,
            order: form.order,
            file_id: form.file_id,
            file_name: form.file_name,
            file_size: form.file_size,
          };
    try {
      if (editing === "new") {
        await api.post("/links", payload);
        toast.success("Link criado");
      } else {
        await api.put(`/links/${editing}`, payload);
        toast.success("Link atualizado");
      }
      cancel();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este link?")) return;
    try {
      await api.delete(`/links/${id}`);
      toast.success("Link removido");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <p className="font-mono text-xs text-zinc-500">
          {data.links.length} link(s) cadastrado(s)
        </p>
        {editing === null && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 rounded-md bg-cyan-400 px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition hover:bg-emerald-400"
            data-testid="add-link-button"
          >
            <Plus className="h-4 w-4" />
            novo link
          </button>
        )}
      </div>

      {editing !== null && (
        <div
          className="mb-6 rounded-xl border border-cyan-400/30 bg-[#0d0d0d] p-6"
          data-testid="link-editor"
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-bold">
              {editing === "new" ? "Novo Link" : "Editar Link"}
            </h3>
            <button
              onClick={cancel}
              className="text-zinc-400 hover:text-red-300"
              data-testid="link-cancel-button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Mode toggle */}
          <div className="mb-4 inline-flex rounded-md border border-white/10 bg-black p-1">
            <button
              type="button"
              onClick={() => setMode("url")}
              className={`px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] rounded ${
                mode === "url"
                  ? "bg-cyan-400 text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="mode-url-toggle"
            >
              URL Externa
            </button>
            <button
              type="button"
              onClick={() => setMode("file")}
              className={`px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] rounded ${
                mode === "file"
                  ? "bg-cyan-400 text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="mode-file-toggle"
            >
              Arquivo APK
            </button>
          </div>

          <Input
            label="Título"
            value={form.title}
            onChange={(v) => setForm({ ...form, title: v })}
            placeholder="Ex: Meu App"
            testId="link-title-input"
          />

          {mode === "url" ? (
            <Input
              label="URL"
              value={form.url}
              onChange={(v) => setForm({ ...form, url: v })}
              placeholder="https://..."
              testId="link-url-input"
            />
          ) : (
            <div className="mb-4">
              <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                Arquivo (.apk até 300MB)
              </label>
              {form.file_id ? (
                <div
                  className="flex items-center justify-between rounded-md border border-emerald-400/30 bg-emerald-400/5 p-3"
                  data-testid="file-attached"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-sm text-emerald-300">
                      {form.file_name}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {formatBytes(form.file_size || 0)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        file_id: null,
                        file_name: null,
                        file_size: null,
                      })
                    }
                    className="ml-3 rounded-md border border-white/10 bg-white/5 p-2 text-zinc-300 hover:border-red-400/40 hover:text-red-300"
                    data-testid="file-clear-button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <label
                  className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-white/15 bg-black p-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 hover:border-cyan-400 hover:text-cyan-300"
                  data-testid="file-upload-label"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? "enviando..." : "selecionar arquivo"}
                  <input
                    type="file"
                    accept=".apk,application/vnd.android.package-archive,application/octet-stream"
                    onChange={onFilePick}
                    className="hidden"
                    disabled={uploading}
                    data-testid="file-upload-input"
                  />
                </label>
              )}
            </div>
          )}

          <Input
            label="Descrição (opcional)"
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            placeholder="curta descrição"
            testId="link-description-input"
          />
          <Input
            label="Ordem"
            type="number"
            value={form.order}
            onChange={(v) => setForm({ ...form, order: parseInt(v || "0") })}
            testId="link-order-input"
          />
          <button
            onClick={save}
            disabled={uploading}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-cyan-400 p-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-black transition hover:bg-emerald-400 disabled:opacity-60"
            data-testid="link-save-button"
          >
            <Save className="h-4 w-4" />
            salvar
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {data.links.length === 0 && editing === null && (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/5 p-8 text-center font-mono text-sm text-zinc-500">
            Nenhum link ainda. Clique em "novo link".
          </div>
        )}
        {data.links.map((link) => (
          <div
            key={link.id}
            className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0d0d0d] p-4"
            data-testid={`link-row-${link.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-display text-sm font-bold">{link.title}</span>
                {link.file_id ? (
                  <span className="rounded border border-emerald-400/40 bg-emerald-400/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-300">
                    APK
                  </span>
                ) : (
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-cyan-300"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <div className="mt-0.5 truncate font-mono text-xs text-zinc-500">
                {link.file_id
                  ? `${link.file_name} • ${formatBytes(link.file_size || 0)}`
                  : link.url}
              </div>
              {link.description && (
                <div className="mt-1 truncate font-mono text-xs text-zinc-400">
                  {link.description}
                </div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => startEdit(link)}
                className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-300"
                data-testid={`link-edit-${link.id}`}
              >
                editar
              </button>
              <button
                onClick={() => remove(link.id)}
                className="rounded-md border border-white/10 bg-white/5 p-2 text-zinc-300 hover:border-red-400/40 hover:text-red-300"
                data-testid={`link-delete-${link.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/* -------- Info Blocks Manager -------- */
function InfoManager({ data, onChange }) {
  const [editing, setEditing] = useState(null);
  const empty = { title: "", content: "", order: 0 };
  const [form, setForm] = useState(empty);

  const startNew = () => {
    setEditing("new");
    setForm({ ...empty, order: data.info_blocks.length });
  };
  const startEdit = (block) => {
    setEditing(block.id);
    setForm({
      title: block.title,
      content: block.content,
      order: block.order ?? 0,
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Preencha título e conteúdo");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/info-blocks", form);
        toast.success("Informação criada");
      } else {
        await api.put(`/info-blocks/${editing}`, form);
        toast.success("Informação atualizada");
      }
      cancel();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta informação?")) return;
    try {
      await api.delete(`/info-blocks/${id}`);
      toast.success("Removida");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <p className="font-mono text-xs text-zinc-500">
          {data.info_blocks.length} bloco(s)
        </p>
        {editing === null && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 rounded-md bg-cyan-400 px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition hover:bg-emerald-400"
            data-testid="add-info-button"
          >
            <Plus className="h-4 w-4" />
            nova info
          </button>
        )}
      </div>

      {editing !== null && (
        <div
          className="mb-6 rounded-xl border border-emerald-400/30 bg-[#0d0d0d] p-6"
          data-testid="info-editor"
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-bold">
              {editing === "new" ? "Nova Informação" : "Editar Informação"}
            </h3>
            <button
              onClick={cancel}
              className="text-zinc-400 hover:text-red-300"
              data-testid="info-cancel-button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Input
            label="Título"
            value={form.title}
            onChange={(v) => setForm({ ...form, title: v })}
            placeholder="Ex: Como funciona"
            testId="info-title-input"
          />
          <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            Conteúdo
          </label>
          <textarea
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={6}
            className="mb-4 w-full resize-none rounded-md border border-white/15 bg-black p-3 font-mono text-sm outline-none transition focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
            placeholder="texto livre..."
            data-testid="info-content-input"
          />
          <Input
            label="Ordem"
            type="number"
            value={form.order}
            onChange={(v) => setForm({ ...form, order: parseInt(v || "0") })}
            testId="info-order-input"
          />
          <button
            onClick={save}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-cyan-400 p-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-black transition hover:bg-emerald-400"
            data-testid="info-save-button"
          >
            <Save className="h-4 w-4" />
            salvar
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {data.info_blocks.length === 0 && editing === null && (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/5 p-8 text-center font-mono text-sm text-zinc-500">
            Nenhuma informação ainda. Clique em "nova info".
          </div>
        )}
        {data.info_blocks.map((block) => (
          <div
            key={block.id}
            className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4"
            data-testid={`info-row-${block.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4 className="font-display text-sm font-bold uppercase tracking-[0.15em] text-emerald-300">
                  {block.title}
                </h4>
                <p className="mt-1 line-clamp-3 whitespace-pre-wrap font-mono text-xs text-zinc-400">
                  {block.content}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  onClick={() => startEdit(block)}
                  className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-300"
                  data-testid={`info-edit-${block.id}`}
                >
                  editar
                </button>
                <button
                  onClick={() => remove(block.id)}
                  className="rounded-md border border-white/10 bg-white/5 p-2 text-zinc-300 hover:border-red-400/40 hover:text-red-300"
                  data-testid={`info-delete-${block.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------- Helper -------- */
function Input({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div className="mb-4">
      <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-white/15 bg-black p-3 font-mono text-sm outline-none transition focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
        data-testid={testId}
      />
    </div>
  );
}
